import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Package, Truck, CheckCircle, UtensilsCrossed, User, MapPin, Calendar, Clock } from 'lucide-react';
import BottomNavigation from '../../components/common/BottomNavigation';
import { useApp } from '../../context/AppContext';
import { useOrder } from '../../context/OrderContext';
import { useAuth } from '../../context/AuthContext';
import { useTable } from '../../context/TableContext';

const CustomerTrack: React.FC = () => {
  const { setCurrentScreen } = useApp();
  const { getCustomerOrders, orders: allOrders } = useOrder();
  const { user } = useAuth();
  const { selectedTable } = useTable();
  const [selectedOrderId, setSelectedOrderId] = useState<string>('');

  // Get orders for the current customer - memoized to prevent unnecessary re-renders
  const orders = useMemo(() => {
    const customerId = user?.customerId || user?.id;
    console.log('🔍 CustomerTrack: User object:', user);
    console.log('🔍 CustomerTrack: Using customerId:', customerId);
    console.log('🔍 CustomerTrack: All orders available:', allOrders.length);
    const result = user && customerId ? getCustomerOrders(customerId) : [];
    console.log('🔍 CustomerTrack: Found orders:', result.length, result);
    return result;
  }, [user, getCustomerOrders, allOrders]); // Added allOrders to dependencies

  // Check for selected order ID from localStorage (when navigating from history)
  React.useEffect(() => {
    console.log('🔍 CustomerTrack: useEffect triggered');
    console.log('🔍 CustomerTrack: Current orders:', orders.map(o => ({ id: o.id, status: o.status })));
    console.log('🔍 CustomerTrack: Current selectedOrderId:', selectedOrderId);
    
    const storedOrderId = localStorage.getItem('selectedOrderId');
    console.log('🔍 CustomerTrack: Stored order ID from localStorage:', storedOrderId);
    
    if (storedOrderId && orders.some(order => order.id === storedOrderId)) {
      console.log('🔍 CustomerTrack: Found matching order, setting selectedOrderId to:', storedOrderId);
      setSelectedOrderId(storedOrderId);
      // Clear the stored order ID after using it
      localStorage.removeItem('selectedOrderId');
    }
    // Always show order selection first, don't auto-select any order
  }, [orders, selectedOrderId]); // Dependencies are now stable due to useMemo

  const selectedOrder = selectedOrderId ? orders.find(order => order.id === selectedOrderId) : null;
  
  // Debug logging
  console.log('🔍 Current selectedOrderId:', selectedOrderId);
  console.log('🔍 Selected order:', selectedOrder);
  console.log('🔍 All orders:', orders.map(o => ({ id: o.id, customerId: o.customerId })));
  console.log('🔍 Will show tracking details?', !!selectedOrder);

  // Generate tracking steps based on order status
  const getTrackingSteps = (orderStatus: string) => {
    const allSteps = [
      { id: 1, status: 'completed', title: 'Order Received', description: 'Your order has been confirmed', time: 'Order confirmed' },
      { id: 2, status: 'completed', title: 'Preparing', description: 'Chef is preparing your food', time: 'In kitchen' },
      { id: 3, status: 'completed', title: 'Ready', description: 'Your food is ready', time: 'Ready to serve' },
      { id: 4, status: 'current', title: 'Serving', description: 'Your order is being served to your table', time: 'On the way' },
      { id: 5, status: 'pending', title: 'Served', description: 'Enjoy your meal!', time: 'Served' }
    ];

    switch (orderStatus) {
      case 'pending':
        return allSteps.map((step, index) => ({
          ...step,
          status: index === 0 ? 'completed' : index === 1 ? 'current' : 'pending'
        }));
      case 'shipped':
        return allSteps.map((step, index) => ({
          ...step,
          status: index <= 2 ? 'completed' : index === 3 ? 'current' : 'pending'
        }));
      case 'delivered':
        return allSteps.map((step, index) => ({
          ...step,
          status: 'completed'
        }));
      case 'cancelled':
        return allSteps.map((step, index) => ({
          ...step,
          status: index === 0 ? 'completed' : 'pending',
          title: index === 1 ? 'Cancelled' : step.title,
          description: index === 1 ? 'Your order has been cancelled' : step.description
        }));
      default:
        return allSteps;
    }
  };

  const trackingSteps = selectedOrder ? getTrackingSteps(selectedOrder.status) : [];
  
  // Debug logging for tracking steps
  console.log('🔍 Tracking steps for selected order:', selectedOrder?.id, trackingSteps);
  console.log('🔍 Order status:', selectedOrder?.status);
  console.log('🔍 Number of tracking steps:', trackingSteps.length);

  const TrackingStep: React.FC<{ step: any; index: number }> = ({ step, index }) => (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: index * 0.1 }}
      className="flex items-start space-x-4"
    >
      <div className="flex flex-col items-center">
        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
          step.status === 'completed' 
            ? 'bg-green-500' 
            : step.status === 'current'
            ? 'bg-teal-500'
            : 'bg-slate-600'
        }`}>
          {step.status === 'completed' ? (
            <CheckCircle size={16} className="text-white" />
          ) : step.status === 'current' ? (
            <Truck size={16} className="text-white" />
          ) : (
            <Package size={16} className="text-slate-400" />
          )}
        </div>
        {index < trackingSteps.length - 1 && (
          <div className={`w-0.5 h-8 mt-2 ${
            step.status === 'completed' ? 'bg-green-500' : 'bg-slate-600'
          }`} />
        )}
      </div>
      <div className="flex-1 pb-6">
        <h3 className={`font-medium ${
          step.status === 'current' ? 'text-teal-400' : 'text-white'
        }`}>
          {step.title}
        </h3>
        <p className="text-slate-400 text-sm">{step.description}</p>
        <p className="text-slate-500 text-xs mt-1">{step.time}</p>
      </div>
    </motion.div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-slate-900 to-gray-900 pb-20">
      {/* Header */}
      <div className="sticky top-0 z-40 backdrop-blur-lg bg-slate-900 bg-opacity-80 border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-4">
            <div className="flex items-center space-x-2 sm:space-x-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-r from-orange-500 to-red-500 rounded-full flex items-center justify-center">
                <UtensilsCrossed className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
              </div>
              <h1 className="text-lg sm:text-xl md:text-2xl font-bold bg-gradient-to-r from-orange-400 to-red-400 bg-clip-text text-transparent">
                Track Order
              </h1>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        {orders.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="food-card text-center py-12"
          >
            <div className="text-6xl mb-4">📦</div>
            <h3 className="text-2xl font-bold text-white mb-2">No Orders to Track</h3>
            <p className="text-gray-400 mb-6">
              You haven't placed any orders yet. Start shopping to track your orders here.
            </p>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setCurrentScreen('customer-home')}
              className="btn-primary py-3 px-8"
            >
              Browse Menu
            </motion.button>
          </motion.div>
        ) : (
          <>
            {/* Order Selection - Only show if no order is selected */}
            {orders.length > 0 && !selectedOrder && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="food-card"
              >
                <h3 className="text-2xl font-bold text-white mb-4">
                  {orders.length > 1 ? 'Select Order to Track' : 'Your Order'}
                </h3>
                {orders.length > 1 && (
                  <div className="mb-4 p-4 bg-slate-700 rounded-xl border border-gray-600">
                    <p className="text-gray-300 text-sm">
                      👆 Please select an order below to view its tracking details
                    </p>
                  </div>
                )}
                <div className="space-y-3">
                  {orders.map((order) => (
                    <button
                      key={order.id}
                      onClick={() => {
                        console.log('🔍 Clicking order:', order.id);
                        console.log('🔍 Current selectedOrderId before update:', selectedOrderId);
                        setSelectedOrderId(order.id);
                        console.log('🔍 Setting selectedOrderId to:', order.id);
                      }}
                      className="w-full text-left p-4 rounded-xl border transition-all duration-200 bg-slate-700 border-gray-600 text-gray-300 hover:bg-slate-600 hover:border-orange-500"
                    >
                      <div className="space-y-3">
                        {/* Order Header */}
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-bold text-white text-lg">Order #{order.id.slice(-8)}</p>
                            <div className="flex items-center space-x-2 text-gray-400 text-sm">
                              <Calendar size={12} />
                              <span>{new Date(order.date).toLocaleDateString()}</span>
                              <Clock size={12} className="ml-2" />
                              <span>{new Date(order.date).toLocaleTimeString('en-US', { 
                                hour: '2-digit', 
                                minute: '2-digit',
                                hour12: true
                              })}</span>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-orange-400 text-xl">${order.amount.toFixed(2)}</p>
                            <p className="text-sm text-gray-400 capitalize">
                              {order.status === 'delivered' ? 'served' :
                               order.status === 'shipped' ? 'ready' :
                               order.status === 'pending' ? 'preparing' :
                               order.status === 'cancelled' ? 'cancelled' :
                               order.status}
                            </p>
                          </div>
                        </div>

                        {/* Customer and Table Info */}
                        <div className="flex items-center justify-between text-sm">
                          <div className="flex items-center space-x-2 text-gray-400">
                            <User size={14} />
                            <span>{order.customerName || user?.name || 'N/A'}</span>
                          </div>
                          {order.tableNumber && (
                            <div className="flex items-center space-x-2 text-gray-400">
                              <MapPin size={14} />
                              <span>{order.tableNumber}</span>
                              {order.tableLocation && (
                                <span className="text-gray-500">({order.tableLocation})</span>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Items Summary */}
                        <div className="text-gray-400 text-sm">
                          {order.items.length} item{order.items.length > 1 ? 's' : ''} • 
                          {order.items.slice(0, 2).map(item => item.productName).join(', ')}
                          {order.items.length > 2 && ` +${order.items.length - 2} more`}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </motion.div>
            )}

            {/* Order Info */}
            {selectedOrder && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="food-card space-y-6"
              >
                {/* Order Header */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="w-16 h-16 bg-gradient-to-r from-orange-500 to-red-500 rounded-full flex items-center justify-center">
                      <Package size={28} className="text-white" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-white">Order #{selectedOrder.id}</h2>
                      <div className="flex items-center space-x-2 text-gray-400 text-sm">
                        <Calendar size={14} />
                        <span>{new Date(selectedOrder.date).toLocaleDateString()}</span>
                        <Clock size={14} className="ml-2" />
                        <span>{new Date(selectedOrder.date).toLocaleTimeString('en-US', { 
                          hour: '2-digit', 
                          minute: '2-digit',
                          hour12: true
                        })}</span>
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      console.log('🔍 Going back to order history');
                      setCurrentScreen('customer-history');
                    }}
                    className="btn-secondary text-sm"
                  >
                    ← Back to Orders
                  </button>
                </div>

                {/* Customer and Table Information */}
                <div className="bg-slate-800 rounded-lg p-4 space-y-3">
                  <h4 className="text-white font-semibold text-lg mb-3">Order Details</h4>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Customer Info */}
                    <div className="space-y-2">
                      <div className="flex items-center space-x-2 text-gray-300">
                        <User size={16} className="text-orange-400" />
                        <span className="font-medium">Customer:</span>
                      </div>
                      <div className="ml-6">
                        <p className="text-white font-semibold">{selectedOrder.customerName || user?.name || 'N/A'}</p>
                        {user?.email && (
                          <p className="text-gray-400 text-sm">{user.email}</p>
                        )}
                        {user?.customerId && (
                          <p className="text-orange-400 text-xs">ID: {user.customerId}</p>
                        )}
                      </div>
                    </div>

                    {/* Table Info */}
                    <div className="space-y-2">
                      <div className="flex items-center space-x-2 text-gray-300">
                        <MapPin size={16} className="text-orange-400" />
                        <span className="font-medium">Table:</span>
                      </div>
                      <div className="ml-6">
                        {selectedOrder.tableNumber ? (
                          <>
                            <p className="text-white font-semibold">{selectedOrder.tableNumber}</p>
                            {selectedOrder.tableLocation && (
                              <p className="text-gray-400 text-sm">{selectedOrder.tableLocation}</p>
                            )}
                          </>
                        ) : (
                          <p className="text-gray-400">No table assigned</p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Order Summary */}
                  <div className="pt-3 border-t border-gray-700">
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="text-gray-400 text-sm">Total Items</p>
                        <p className="text-white font-semibold">{selectedOrder.items.length} items</p>
                      </div>
                      <div className="text-right">
                        <p className="text-gray-400 text-sm">Total Amount</p>
                        <p className="text-orange-400 font-bold text-xl">${selectedOrder.amount.toFixed(2)}</p>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Order Items */}
                <div className="bg-slate-800 rounded-lg p-4">
                  <h4 className="text-white font-semibold text-lg mb-3">Items Ordered</h4>
                  <div className="space-y-3">
                    {selectedOrder.items.map((item, index) => (
                      <div key={index} className="flex justify-between items-center p-3 bg-slate-700 rounded-lg">
                        <div className="flex-1">
                          <p className="text-white font-medium">{item.productName}</p>
                          <p className="text-gray-400 text-sm">Quantity: {item.quantity}</p>
                          <p className="text-orange-400 text-sm">${item.price.toFixed(2)} each</p>
                        </div>
                        <div className="text-right">
                          <p className="text-white font-bold text-lg">${(item.price * item.quantity).toFixed(2)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            )}
          </>
        )}

        {/* Tracking Steps */}
        {orders.length > 0 && selectedOrder && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="food-card"
          >
            <h3 className="text-2xl font-bold text-white mb-6">Order Status</h3>
            <div className="space-y-4">
              {trackingSteps.map((step, index) => (
                <TrackingStep key={step.id} step={step} index={index} />
              ))}
            </div>
          </motion.div>
        )}

      </div>

      <BottomNavigation />
    </div>
  );
};

export default CustomerTrack;
